import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Drawer,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Fab,
  Zoom,
  Paper,
  Button,
  Alert,
  Snackbar,
  Backdrop,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import { LoaderWrapper } from '../../components/index';

// Import all JD sections
import AppDashboard from './application/AppDashboard';
import UploadMOM from './application/UploadMOM';
import AgendaDate from './application/AgendaDate';
import ACDates from './application/ACDates';


const DRAWER_WIDTH = 320;
const COLLAPSED_DRAWER_WIDTH = 70;
const APP_HEADER_HEIGHT = 64; // Height of the existing ApplicationHeader

const ConvenerLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [currentSection, setCurrentSection] = useState('convener-applications');
  const [formData, setFormData] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);

  // Section configuration
  const sections = useMemo(() => [
    {
      id: 'convener-applications',
      title: 'Convener Applications',
      component: AppDashboard,
      path: '/main/convener/dashboard',
    },
    {
      id: 'convener-ac-dates',
      title: 'AC Dates',
      component: ACDates,
      path: '/main/convener/ac-dates',
    },
    {
      id: 'convener-agenda-date',
      title: 'Agenda Date',
      component: AgendaDate,
      path: '/main/convener/agenda-date',
    },
    {
      id: 'convener-upload-mom',
      title: 'Upload MOM',
      component: UploadMOM,
      path: '/main/convener/upload-mom',
    }
  ], []);

  // Determine current section based on route
  useEffect(() => {
    const currentPath = location.pathname;
    const section = sections.find(s => s.path === currentPath);
    if (section) {
      setCurrentSection(section.id);
    }
  }, [location.pathname, sections]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleFormDataUpdate = async (sectionId, data) => {
    const updatedFormData = {
      ...formData,
      [sectionId]: {
        ...formData[sectionId],
        ...data,
      },
    };
    
    setFormData(updatedFormData);
  };

  const navigateToSection = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (section) {
      navigate(section.path);
    }
  };

  const renderCurrentSection = () => {
    console.log('🔍 JDLayout renderCurrentSection:', {
      currentSection,
      pathname: location.pathname,
      availableSections: sections.map(s => ({ id: s.id, path: s.path }))
    });
    
    const section = sections.find(s => s.id === currentSection);
    if (!section) {
      console.log('❌ Section not found for currentSection:', currentSection);
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            Section not found: {currentSection}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Current path: {location.pathname}
          </Typography>
        </Box>
      );
    }

    console.log('✅ Rendering section:', section.title, section.component.name);
    const SectionComponent = section.component;
    
    return (
      <SectionComponent
        data={formData[currentSection] || {}}
        onUpdate={(data) => handleFormDataUpdate(currentSection, data)}
      />
    );
  };

  const drawerWidth = sidebarCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH;

  return (
    <Box sx={{ display: 'flex', height: '100vh', pt: `${APP_HEADER_HEIGHT}px` }}>
      {/* Sidebar */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid #e0e0e0',
            height: `calc(100vh - ${APP_HEADER_HEIGHT}px)`,
            top: `${APP_HEADER_HEIGHT}px`,
            display: 'flex',
            flexDirection: 'column',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.standard,
            }),
            overflowX: 'hidden',
          },
        }}
      >
        <Sidebar
          isMobile={isMobile}
          onClose={() => setSidebarOpen(false)}
          activeStep={0}
          onSectionClick={(item) => navigate(item.path)}
          collapsed={sidebarCollapsed}
          onCollapseToggle={handleSidebarCollapse}
        />
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { lg: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { lg: sidebarOpen ? 0 : 0 },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          position: 'relative',
        }}
      >
        {/* Toggle Sidebar Button for Mobile when Sidebar is Closed */}
        {isMobile && !sidebarOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: `${APP_HEADER_HEIGHT + 10}px`,
              left: 10,
              zIndex: theme.zIndex.drawer + 1,
            }}
          >
            <IconButton
              onClick={handleSidebarToggle}
              sx={{
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}

        <Container maxWidth="xl" sx={{ py: 3, height: '100%' }}>
          {/* Loading Indicator */}
          {loading && (
            <LoaderWrapper 
              loading={true} 
              type="page" 
              message="Loading application data..." 
            />
          )}

          <Paper sx={{ minHeight: 'calc(100vh - 280px)', mb: 3 }}>
            {renderCurrentSection()}
          </Paper>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Save Status Indicator */}
          {saveLoading && (
            <LoaderWrapper 
              loading={true} 
              type="inline" 
              message="Saving form data..." 
              size="small"
            />
          )}

        </Container>
      </Box>

      {/* Save Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Form data saved successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConvenerLayout;
